// socketManager.js — FIXED VERSION WITH CORRECT MESSAGE ORDER
import { apiService } from "../services/apiService.js";
let transcribeSocket = null;
let interviewSocket = null;
let countdownCompleted = false;
let interviewPingInterval = null;

// ─── Interview-socket lock ────────────────────────────────────────────────

let interviewSocketLocked =
  sessionStorage.getItem("interviewSocketLocked") === "true";

export const lockInterviewSocket = (reason = "") => {
  if (interviewSocketLocked) return;
  interviewSocketLocked = true;
  sessionStorage.setItem("interviewSocketLocked", "true");
  console.log(
    "[Socket] Interview socket permanently locked" +
      (reason ? ` (${reason})` : ""),
  );
};

export const isInterviewSocketLocked = () => interviewSocketLocked;
// ────────────────────────────────────────────────────────────────────────

// ─── Ping-time network telemetry ─────────────────────────────────────────
// There is no browser API for real upload throughput short of running an
// active transfer test, which we deliberately avoid doing every 90s during
// a live interview (it would compete for bandwidth with the actual
// audio/video/whiteboard traffic). Instead we attach:
//  - downlinkMbps/effectiveType/apiRtt: best-effort estimates from the
//    Network Information API (Chrome/Edge/Android only — undefined on
//    Safari/Firefox, so these fields may come back null).
//  - measuredRttMs: real round-trip time computed from our own ping→pong,
//    which works everywhere and reflects the actual socket path.
let lastPingSentAt = null;
let lastPingRttMs = null;

// Chrome/Edge only — no Firefox/Safari fallback needed since the app
// targets Chromium browsers exclusively.
const getConnectionSnapshot = () => {
  const conn = navigator.connection;
  if (!conn) {
    return {
      downlinkMbps: null,
      downlinkMaxMbps: null,
      effectiveType: null,
      apiRtt: null,
      saveData: null,
    };
  }
  return {
    downlinkMbps: typeof conn.downlink === "number" ? conn.downlink : null,
    // Theoretical max for the underlying connection type (e.g. wifi vs
    // cellular) — Infinity when unknown/unbounded, so normalize that to null.
    downlinkMaxMbps:
      typeof conn.downlinkMax === "number" && Number.isFinite(conn.downlinkMax)
        ? conn.downlinkMax
        : null,
    effectiveType: conn.effectiveType || null,
    apiRtt: typeof conn.rtt === "number" ? conn.rtt : null,
    // True if the candidate has "Data Saver" enabled — a real signal they
    // may be on a constrained/metered connection.
    saveData: typeof conn.saveData === "boolean" ? conn.saveData : null,
  };
};

const buildPingPayload = async () => {
  lastPingSentAt = Date.now();
  return JSON.stringify({
    type: "ping",
    ts: lastPingSentAt,
    network: {
      ...getConnectionSnapshot(),
      measuredRttMs: lastPingRttMs, // from the previous ping's pong; null on the very first ping
    },
  });
};

let mediaStatsProvider = null;

export const setMediaStatsProvider = (fn) => {
  mediaStatsProvider = typeof fn === "function" ? fn : null;
};

const getMediaStatsSnapshot = async () => {
  if (!mediaStatsProvider) return null;
  try {
    // Expected shape: { audio: {jitter, bitrate, packetsLost, totalPackets, rtt, network}, video: {...same, size} }
    const stats = await mediaStatsProvider();
    return stats || null;
  } catch (e) {
    console.warn(
      " mediaStatsProvider failed, omitting media stats from ping",
      e,
    );
    return null;
  }
};

// When true, the onclose/onerror handlers will NOT fire the
// "interview-socket-closed" / "interview-socket-error" window events.
// Interview.js sets this to true at the start of a reconnect grace period
// so that a transient server-side close doesn't double-trigger the offline flow.
let suppressSocketCloseEvents = false;

export const setSocketCloseSuppressed = (value) => {
  suppressSocketCloseEvents = !!value;
};

let interviewQueue = [];
let transscribePingInterval = null;
let botAllowedToStart = false;

let publishBotMessage = null;
let publishChatMessage = null;

let lastPanelSenderId = null;
let pendingSpeakerQueue = [];

let messageWaitQueue = [];
let canProcess = false;
let waitingProcessorRunning = false;
let eofReceived = false;
let botResponseSeq = 0;

// ─── Whiteboard post-LST hold gate ───────────────────────────────────────
// Sequence: FBK → (if whiteboarding_env === "true") whiteboard opens → LST
// arrives → the final bot text message + its binary/audio blob arrive next.
// While the whiteboard is open we don't want that final message/audio (or
// the EOI/redirect it carries) to be processed yet — the candidate is still
// drawing. So once LST is seen, if the whiteboard flag is true, every raw
// socket message that follows is buffered here in arrival order instead of
// being handed to the normal onmessage logic. The instant the candidate
// submits the whiteboard (flag flips to "false", "whiteboard-submitted"
// fires), the buffer is replayed in order through the exact same code path,
// so the last message + binary play, and only THEN does end-of-interview /
// redirect logic run — same as if the whiteboard had never been there.
let postLstHoldActive = false;
let heldInterviewEvents = [];

// handleInterviewSocketMessage is defined *inside* connectInterviewSocket
// (fresh closure per socket connection), so flushHeldInterviewEvents — a
// module-level function that exists before any socket is even opened —
// has no way to call it directly. This ref is populated by
// connectInterviewSocket every time it (re)defines the handler, so the
// flush below always calls the handler for the CURRENT socket instance.
let currentHandleInterviewSocketMessage = null;

const isWhiteboardActive = () =>
  sessionStorage.getItem("whiteboarding_env") === "true";
const isCodingActive = () => sessionStorage.getItem("coding_env") === "true";
const flushHeldInterviewEvents = async () => {
  if (isWhiteboardActive() || isCodingActive()) {
    console.log(
      "[Socket] Not flushing held events yet, whiteboard or coding is still active",
    );
    return;
  }
  postLstHoldActive = false;

  if (heldInterviewEvents.length === 0) return;

  console.log(
    `[Socket] Whiteboard closed — flushing ${heldInterviewEvents.length} held post-LST message(s)`,
  );
  const toFlush = heldInterviewEvents;
  heldInterviewEvents = [];

  if (!currentHandleInterviewSocketMessage) {
    console.warn(
      "[Socket] No active message handler to flush held events into — dropping",
      toFlush.length,
      "event(s)",
    );
    return;
  }

  // Replay sequentially (not in parallel) so ordering/state mutations stay
  // exactly the way they would have been without the hold. This is what
  // actually plays the final bot message + its audio binary after the
  // whiteboard is submitted.
  for (const event of toFlush) {
    // eslint-disable-next-line no-await-in-loop
    await currentHandleInterviewSocketMessage(event);
  }
};

window.addEventListener("whiteboard-submitted", flushHeldInterviewEvents);
window.addEventListener("coding-submitted", flushHeldInterviewEvents);

// True once "LST" has arrived on the interview socket. LST fires before the
// final bot message + its audio binary (and, when a whiteboard round is in
// play, before EOI too — see the postLstHoldActive gate above). A socket
// close/error after LST is expected — the server has already sent
// everything it needs to — so this is checked the same way eoiReceived is,
// to stop close/error handlers from raising a false "technical issue" for
// what's actually a normal end-of-interview close that happened to land
// before EOI got flushed out of the whiteboard hold.
let lstReceived = false;

// True once an actual "EOI" (end-of-interview) message has arrived on the
// interview socket. Used to distinguish a *normal* end-of-interview socket
// close from a socket that died unexpectedly mid-interview.
let eoiReceived = false;

// Reset when a fresh interview socket is set up (new session / reconnect),
// so a stale flag from a previous socket instance never leaks forward.
export const resetEOIReceived = () => {
  eoiReceived = false;
  lstReceived = false;
  postLstHoldActive = false;
  heldInterviewEvents = [];
};

export const isEOIReceived = () => eoiReceived;
export const isLSTReceived = () => lstReceived;

// Decrement wait queue only after audio finishes

const processWaitQueue = () => {
  if (!countdownCompleted) return;
  if (waitingProcessorRunning) return;

  waitingProcessorRunning = true;

  while (messageWaitQueue.length > 0) {
    const next = messageWaitQueue.shift();
    enqueueBotMessage(next);
  }

  waitingProcessorRunning = false;
  let total = messageWaitQueue.length + messagePublishQueue.length;

  if (!eofReceived && total === 0) {
    total = 1;
  }
  window.dispatchEvent(
    new CustomEvent("bot-wait-queue-status", {
      detail: {
        messageWaitQueue: total,
      },
    }),
  );
};
const safeSend = (socket, data, retries = 5) => {
  if (!socket) return;

  if (socket.readyState === WebSocket.OPEN) {
    socket.send(data);
  } else if (retries > 0) {
    setTimeout(() => safeSend(socket, data, retries - 1), 300);
  } else {
    console.warn("WebSocket send failed after retries");
  }
};
export const setBotMessagePublisher = (publisherFn) => {
  publishBotMessage = publisherFn;
};

export const allowBotToStart = () => {
  botAllowedToStart = true;
};

export const setChatMessagePublisher = (publisherFn) => {
  publishChatMessage = publisherFn;
};

window.addEventListener("countdown-complete", () => {
  countdownCompleted = true;
  processWaitQueue();
});

export const handleEOIMessage = (message) => {
  if (
    message.trim() === "EOI" ||
    message.toLowerCase().includes("end of interview") ||
    message.toLowerCase().includes("eoi")
  ) {
    eoiReceived = true;
    window.dispatchEvent(
      new CustomEvent("end-interview-triggered", {
        detail: {
          message: message,
          timestamp: new Date().toISOString(),
        },
      }),
    );
    return true;
  }
  return false;
};

let messagePublishQueue = [];
let isPublishing = false;

const enqueueBotMessage = (botMessage) => {
  messagePublishQueue.push(botMessage);
  let total = messageWaitQueue.length + messagePublishQueue.length;

  if (!eofReceived && total === 0) {
    total = 1;
  }
  window.dispatchEvent(
    new CustomEvent("bot-wait-queue-status", {
      detail: {
        messageWaitQueue: total,
      },
    }),
  );

  if (!isPublishing) {
    processBotMessageQueue();
  }
};

// Maximum time to wait for bot-finished-speaking before publishing anyway.
// Acts as a safety net so the queue never permanently stalls.
const BOT_SPEAK_PUBLISH_TIMEOUT_MS = 30000;

const processBotMessageQueue = async () => {
  if (messagePublishQueue.length === 0) {
    isPublishing = false;
    return;
  }

  isPublishing = true;
  const nextMessage = messagePublishQueue.shift();
  let total = messageWaitQueue.length + messagePublishQueue.length;

  if (!eofReceived && total === 0) {
    total = 1;
  }
  window.dispatchEvent(
    new CustomEvent("bot-wait-queue-status", {
      detail: {
        messageWaitQueue: total,
      },
    }),
  );

  // Wait for the bot to finish speaking the current audio chunk before
  // publishing this transcript to the chat panel.
  const publishAfterSpeaking = () => {
    if (publishBotMessage) {
      publishBotMessage({
        ...nextMessage,
        message: JSON.stringify(nextMessage),
      });
    }
    processBotMessageQueue();
  };

  let fallbackTimer = null;

  const onBotFinished = () => {
    clearTimeout(fallbackTimer);
    publishAfterSpeaking();
  };

  window.addEventListener("bot-finished-speaking", onBotFinished, {
    once: true,
  });

  // Fallback: if bot-finished-speaking never fires (e.g. audio error, no audio
  // blob for this message), publish after the timeout so the queue doesn't stall.
  fallbackTimer = setTimeout(() => {
    window.removeEventListener("bot-finished-speaking", onBotFinished);
    publishAfterSpeaking();
  }, BOT_SPEAK_PUBLISH_TIMEOUT_MS);
};

export const sendPeripheralRecord = async () => {
  try {
    const userLogsRaw = sessionStorage.getItem("userInteractionLogs");
    const interviewId = sessionStorage.getItem("interviewId");

    if (!interviewId || !userLogsRaw) {
      console.warn("Missing interview_id or userInteractionLogs");
      return;
    }

    const userLogs = JSON.parse(userLogsRaw);

    await apiService.setInterviewPeripheralRecord(interviewId, userLogs);
  } catch (err) {
    console.error("Failed to send peripheral record:", err);
  }
};
export const connectInterviewSocket = (payload) => {
  if (interviewSocketLocked) {
    console.warn(
      "[Socket] connectInterviewSocket: interview socket is locked (coding/whiteboard active) — refusing to connect",
    );
    return;
  }

  if (
    interviewSocket &&
    (interviewSocket.readyState === WebSocket.OPEN ||
      interviewSocket.readyState === WebSocket.CONNECTING)
  ) {
    console.warn(
      "[Socket] connectInterviewSocket: a socket is already",
      interviewSocket.readyState === WebSocket.OPEN ? "OPEN" : "CONNECTING",
      "— skipping duplicate connect",
    );
    return;
  }

  const panelDetails = JSON.parse(
    sessionStorage.getItem("panelDetails") || "{}",
  );

  let panelCode = (panelDetails?.panel || "PNLO").toLowerCase();
  const validPanels = ["pnlo", "pnla", "pnlb"];
  if (!validPanels.includes(panelCode)) panelCode = "pnlo";

  const socketUrl = `${process.env.REACT_APP_WEB_SOCKET_URL}api/interview/${panelCode}`;

  // Fresh socket for a fresh interview session — clear any stale EOI flag
  // left over from a previous socket instance.
  resetEOIReceived();

  interviewSocket = new WebSocket(socketUrl);
  interviewSocket.binaryType = "blob";

  // ── TEST-ONLY: force-close the socket 40s after it opens, to verify the
  // "closed before EOI → technical issue" flow. DELETE this whole block
  // (from here to the matching "END TEST-ONLY" marker) once verified. ──
  // const __testForceCloseSocket = interviewSocket;
  // const __testForceCloseTimer = setTimeout(() => {
  //   if (
  //     __testForceCloseSocket === interviewSocket &&
  //     interviewSocket?.readyState === WebSocket.OPEN &&
  //     !eoiReceived
  //   ) {
  //     console.warn(
  //       "[TEST] Force-closing interview socket 40s after open (eoiReceived=false)",
  //     );
  //     interviewSocket.close(4999, "TEST: forced close before EOI");
  //   }
  // }, 40000);
  // // ── END TEST-ONLY ──

  interviewSocket.onopen = () => {
    // FIX: Resolve session_id with explicit priority:
    //   1. _reconnect_session_id passed by _reconnectAndSend (extracted from the
    //      outgoing message — most reliable, avoids race with global being cleared)
    //   2. window.__qaSessionId (set by startQARecording — may still be live)
    //   3. "" (safe fallback for initial connection before any Q&A)
    // Strip the internal _reconnect_session_id key before sending on the wire.
    const { _reconnect_session_id, ...wirePayload } = payload;
    const sessionId = _reconnect_session_id || window.__qaSessionId || "";

    const enrichedPayload = {
      ...wirePayload,
      session_id: sessionId,
      reconnect: !!sessionId, // true only if reconnect
    };

    console.log("[Socket] Sending payload:", enrichedPayload);

    safeSend(interviewSocket, JSON.stringify(enrichedPayload));

    //  clear old interval
    if (interviewPingInterval) clearInterval(interviewPingInterval);

    //  send one ping immediately (so you can verify right away)
    try {
      buildPingPayload().then((msg) => safeSend(interviewSocket, msg));
    } catch (e) {
      console.warn(" Immediate ping failed", e);
    }

    //  ping every 90 seconds
    interviewPingInterval = setInterval(() => {
      try {
        if (interviewSocket?.readyState === WebSocket.OPEN) {
          buildPingPayload().then((msg) => safeSend(interviewSocket, msg));
        } else {
          console.warn(
            "Interview socket not OPEN, ping skipped",
            interviewSocket?.readyState,
          );
        }
      } catch (e) {
        console.warn("Ping send error", e);
      }
    }, 90 * 1000);

    window.dispatchEvent(
      new CustomEvent("interview-socket-connected", { detail: true }),
    );
  };

  interviewSocket.onmessage = async (event) => {
    // If we're mid-hold (LST already seen, whiteboard still open), every
    // subsequent message — text or binary — gets buffered instead of
    // processed. It'll be replayed in order once the whiteboard is submitted.
    if (postLstHoldActive) {
      heldInterviewEvents.push(event);
      console.log(
        "[Socket] Whiteboard open — holding post-LST message:",
        typeof event.data === "string" ? event.data : "[binary blob]",
      );
      return;
    }

    await handleInterviewSocketMessage(event);
  };

  const handleInterviewSocketMessage = async (event) => {
    if (typeof event.data === "string") {
      try {
        let transcript = "";
        try {
          const data = JSON.parse(event.data);
          if (data?.type === "pong") {
            if (lastPingSentAt) {
              lastPingRttMs = Date.now() - lastPingSentAt;
            }
            return;
          }
          transcript = data.message || data.text || data.response || "";
        } catch {
          transcript = event.data;
        }

        transcript = transcript.trim();
        if (transcript === "EOF") {
          eofReceived = true;

          window.dispatchEvent(
            new CustomEvent("bot-wait-queue-status", {
              detail: {
                messageWaitQueue: 0,
              },
            }),
          );

          return;
        }
        if (transcript.toUpperCase() === "RNNG") {
          console.warn(
            "RNNG received - Interview already running in another tab.",
          );

          // Close socket immediately
          if (interviewPingInterval) {
            clearInterval(interviewPingInterval);
            interviewPingInterval = null;
          }

          if (
            interviewSocket &&
            interviewSocket.readyState === WebSocket.OPEN
          ) {
            interviewSocket.close(4000, "Interview already running");
          }

          // Notify UI
          window.dispatchEvent(new CustomEvent("interview-already-running"));

          return; // STOP processing anything else
        }

        if (transcript.toUpperCase() === "CMPLTD") {
          console.warn(
            "CMPLTD received - interview already completed (e.g. in another tab/session).",
          );

          // Stop pinging and close the socket, same as RNNG handling.
          if (interviewPingInterval) {
            clearInterval(interviewPingInterval);
            interviewPingInterval = null;
          }

          if (
            interviewSocket &&
            interviewSocket.readyState === WebSocket.OPEN
          ) {
            interviewSocket.close(4001, "Interview already completed");
          }

          // Let the UI show a banner and redirect to the end-interview screen
          // instead of treating this as a normal chat/bot message.
          window.dispatchEvent(new CustomEvent("interview-already-completed"));

          return; // STOP processing anything else
        }

        if (transcript.toUpperCase() === "FBK") {
          sendPeripheralRecord();
          console.warn("FBK received. Stopping interview ping loop.");

          if (interviewPingInterval) {
            clearInterval(interviewPingInterval);
            interviewPingInterval = null;
          }

          // Snapshot the last active session_id BEFORE setting __FBK_RECEIVED__.
          // Feedback transcripts ("It was good.", etc.) arrive after FBK, so
          // __qaSessionId will be "" by then. __fbkSessionId carries the correct
          // ID forward so those messages are still grouped correctly server-side.
          window.__fbkSessionId = window.__qaSessionId || "";
          console.log(
            "[Socket] FBK — snapshotted session_id for feedback messages:",
            window.__fbkSessionId,
          );

          window.__FBK_RECEIVED__ = true;
          sessionStorage.setItem("fbk-received", "true");
          window.dispatchEvent(new CustomEvent("fbk-received"));
          return;
        }

        const panelDetails = JSON.parse(
          sessionStorage.getItem("panelDetails") || "{}",
        );

        if (/^PNL[A-Z]\d+$/i.test(transcript)) {
          lastPanelSenderId = transcript;
          pendingSpeakerQueue.push(transcript);

          // window.dispatchEvent(
          //   new CustomEvent("panel-speaking", {
          //     detail: { speakerId: transcript },
          //   }),
          // );

          return;
        }

        if (transcript.toUpperCase() === "LST") {
          lstReceived = true;
          window.dispatchEvent(new CustomEvent("lst-received"));

          if (isWhiteboardActive() || isCodingActive()) {
            console.log(
              "[Socket] LST received while whiteboard is open — holding everything after this until submit",
            );
            postLstHoldActive = true;
          }
          return;
        }

        const isEOI = handleEOIMessage(transcript);
        if (isEOI) return;

        let senderId = "Panel Member";
        let senderName =
          panelDetails?.pnlf1 ||
          panelDetails?.pnlf2 ||
          panelDetails?.pnlm1 ||
          panelDetails?.pnlm2 ||
          "Panel";

        if (panelDetails?.panel && panelDetails.panel !== "PNLO") {
          if (lastPanelSenderId) {
            const lookupKey = lastPanelSenderId.toLowerCase();
            senderId = lastPanelSenderId;
            senderName = panelDetails[lookupKey] || lastPanelSenderId;
          }
        }

        const botMessage = {
          senderId,
          senderName,
          message: transcript,
          timestamp: new Date().toISOString(),
          isBot: true,
        };

        // NEW — correct queueing
        eofReceived = false;
        messageWaitQueue.push(botMessage);
        processWaitQueue();
      } catch (err) {
        console.error("Error handling interview socket text:", err);
      }
    } else if (event.data instanceof Blob) {
      try {
        const panelDetails = JSON.parse(
          sessionStorage.getItem("panelDetails") || "{}",
        );

        let speakerId =
          pendingSpeakerQueue.length > 0
            ? pendingSpeakerQueue.shift()
            : lastPanelSenderId || "Panel Member";

        let speakerName =
          panelDetails?.pnlf1 ||
          panelDetails?.pnlf2 ||
          panelDetails?.pnlm1 ||
          panelDetails?.pnlm2 ||
          "Panel";

        if (panelDetails?.panel && panelDetails.panel !== "PNLO") {
          const lookupKey = speakerId?.toLowerCase();
          if (lookupKey && panelDetails[lookupKey]) {
            speakerName = panelDetails[lookupKey];
          }
        }

        const detail = {
          transcript: "Bot response",
          messageId: ++botResponseSeq,
          audioBlob: event.data,
          audioFormat: "mp3",
          source: "interview",
          speakerId,
          speakerName,
        };

        window.dispatchEvent(new CustomEvent("bot-response", { detail }));
      } catch (err) {
        console.error("Error handling binary message:", err);
      }
    }
  };

  // Expose this connection's handler to the module-level flush gate so a
  // whiteboard submit can replay any events it held (final bot message +
  // its audio binary).
  currentHandleInterviewSocketMessage = handleInterviewSocketMessage;

  interviewSocket.onerror = (err) => {
    console.error(" Interview socket error", err);
    if (suppressSocketCloseEvents) {
      console.log("[Socket] Error suppressed during reconnect grace period");
      return;
    }
    window.dispatchEvent(
      new CustomEvent("interview-socket-error", { detail: err.message }),
    );
  };

  interviewSocket.onclose = () => {
    console.warn(
      `Interview socket closed (eoiReceived: ${eoiReceived}, lstReceived: ${lstReceived})`,
    );
    if (suppressSocketCloseEvents) {
      console.log("[Socket] Close suppressed during reconnect grace period");
      return;
    }
    // IMPORTANT: dispatch interview-socket-closed BEFORE disable-interview-guard.
    // disable-interview-guard flips ignoreSocketClosureRef.current to true in
    // Interview.js, and that ref never resets — dispatching it first would
    // make onSocketClose bail out on every close, including this first one.
    window.dispatchEvent(
      new CustomEvent("interview-socket-closed", {
        detail: { eoiReceived, lstReceived },
      }),
    );
    window.dispatchEvent(new CustomEvent("disable-interview-guard"));
  };
};

/**
 * Reconnect the interview socket after a network recovery.
 *
 * Strategy:
 *  1. If the existing socket is OPEN, send a ping and wait up to PONG_TIMEOUT_MS
 *     for a pong response.
 *       → Pong received  : socket is alive, keep it and do nothing.
 *       → No pong / error: fall through and open a fresh socket.
 *  2. If the socket is already CLOSED / null, open a fresh socket immediately.
 */
const PONG_TIMEOUT_MS = 4000; // how long to wait for a pong before giving up

export const reconnectInterviewSocket = () => {
  if (interviewSocketLocked) {
    console.warn(
      "[Socket] reconnectInterviewSocket: interview socket is locked (coding/whiteboard active) — skipping",
    );
    return;
  }

  const interviewId = sessionStorage.getItem("interviewId");
  const companyId = sessionStorage.getItem("companyId");

  if (!interviewId) {
    console.warn(
      "[Socket] reconnectInterviewSocket: no interviewId in session",
    );
    return;
  }

  const openFreshSocket = () => {
    // Tear down whatever is there
    if (interviewSocket) {
      try {
        interviewSocket.onclose = null;
        interviewSocket.onerror = null;
        if (
          interviewSocket.readyState === WebSocket.OPEN ||
          interviewSocket.readyState === WebSocket.CONNECTING
        ) {
          interviewSocket.close(1000, "Reconnecting");
        }
      } catch (e) {
        // ignore
      }
      interviewSocket = null;
    }

    if (interviewPingInterval) {
      clearInterval(interviewPingInterval);
      interviewPingInterval = null;
    }

    suppressSocketCloseEvents = false;
    console.log("[Socket] Opening fresh interview socket…");
    connectInterviewSocket({
      interview_id: interviewId,
      company_id: companyId,
      meeting_room_id: sessionStorage.getItem("meetingId"),
    });
  };

  // ── CASE 1: socket is not open at all — go straight to fresh connect ──
  if (!interviewSocket || interviewSocket.readyState !== WebSocket.OPEN) {
    console.log(
      "[Socket] Socket not OPEN (state:",
      interviewSocket?.readyState ?? "null",
      ") — opening fresh socket",
    );
    openFreshSocket();
    return;
  }

  // ── CASE 2: socket reports OPEN — ping it to verify it's truly alive ──
  console.log("[Socket] Socket appears OPEN — sending probe ping…");

  let pongReceived = false;
  let pongTimeout = null;

  // Temporarily attach a one-shot message listener that watches for the pong
  const originalOnMessage = interviewSocket.onmessage;

  const pongListener = (event) => {
    if (typeof event.data === "string") {
      try {
        const data = JSON.parse(event.data);
        if (data?.type === "pong") {
          pongReceived = true;
          clearTimeout(pongTimeout);
          // Restore the real message handler
          interviewSocket.onmessage = originalOnMessage;
          console.log(
            "[Socket] Probe pong received — existing socket is alive, keeping it",
          );
          suppressSocketCloseEvents = false;
          window.dispatchEvent(
            new CustomEvent("interview-socket-connected", { detail: true }),
          );
          return;
        }
      } catch {
        // not JSON or not a pong — fall through to original handler
      }
    }
    // Forward non-pong messages to the real handler so nothing is lost
    originalOnMessage?.(event);
  };

  interviewSocket.onmessage = pongListener;

  // Send the probe ping
  try {
    interviewSocket.send(JSON.stringify({ type: "ping", ts: Date.now() }));
  } catch (e) {
    console.warn("[Socket] Probe ping send failed — opening fresh socket", e);
    interviewSocket.onmessage = originalOnMessage;
    openFreshSocket();
    return;
  }

  // If no pong arrives within PONG_TIMEOUT_MS, the socket is a zombie — replace it
  pongTimeout = setTimeout(() => {
    if (!pongReceived) {
      console.warn(
        `[Socket] No pong within ${PONG_TIMEOUT_MS}ms — socket is zombie, opening fresh socket`,
      );
      interviewSocket.onmessage = originalOnMessage;
      openFreshSocket();
    }
  }, PONG_TIMEOUT_MS);
};

// ===========================
// Transcribe socket
// ===========================
export const getTranscribeSocket = () => {
  if (!transcribeSocket || transcribeSocket.readyState > 1) {
    transcribeSocket = new WebSocket(
      `${process.env.REACT_APP_WEB_SOCKET_URL}api/transcribe`,
    );
    transcribeSocket.binaryType = "arraybuffer";

    transcribeSocket.onmessage = async (event) => {
      try {
        if (typeof event.data === "string") {
          const data = JSON.parse(event.data);
          const transcriptionText = data.text || data.transcript || "";
          if (transcriptionText.trim()) {
            window.dispatchEvent(
              new CustomEvent("user-transcription", {
                detail: {
                  transcript: transcriptionText,
                  segments: data.segments || [],
                },
              }),
            );
            sendInterviewMessage({ message: transcriptionText.trim() });
          }
        }
      } catch (error) {
        console.error(" Error parsing transcribe message:", error);
      }
    };

    transcribeSocket.onerror = (err) =>
      console.error(" Transcribe socket error", err);

    transcribeSocket.onclose = () => {
      console.warn(" Transcribe WebSocket closed");
      transcribeSocket = null;
    };
  }

  return transcribeSocket;
};

// ===========================
// Cleanup + utilities
// ===========================
export const sendInterviewMessage = (message) => {
  let payload = { message: "", session_id: "" };

  if (typeof message === "string") {
    payload.message = message;
    payload.session_id =
      (typeof window !== "undefined" && window.__qaSessionId) ||
      (typeof window !== "undefined" && window.__fbkSessionId) ||
      (typeof window !== "undefined" && window.__lastKnownSessionId) ||
      "";
  } else if (message && typeof message === "object") {
    payload = {
      ...payload,
      ...message,
      message: message.message || "",
      session_id:
        message.hasOwnProperty("session_id") && message.session_id !== undefined
          ? message.session_id
          : (typeof window !== "undefined" && window.__qaSessionId) ||
            (typeof window !== "undefined" && window.__fbkSessionId) ||
            (typeof window !== "undefined" && window.__lastKnownSessionId) ||
            "",
    };
  }

  // ── session_id guard ──────────────────────────────────────────────────────
  // If session_id is still empty, poll for up to SESSION_ID_WAIT_MS (3 s) in
  // 100 ms increments before sending. This covers the race where the transcript
  // fires just before startQARecording() has written window.__qaSessionId.
  const SESSION_ID_WAIT_MS = 3000;
  const SESSION_ID_POLL_MS = 100;

  if (!payload.session_id) {
    console.warn(
      "[Socket] sendInterviewMessage: session_id empty — waiting for startQARecording to set it…",
    );

    let waited = 0;
    const pollAndSend = () => {
      const liveId =
        (typeof window !== "undefined" && window.__qaSessionId) ||
        (typeof window !== "undefined" && window.__fbkSessionId) ||
        (typeof window !== "undefined" && window.__lastKnownSessionId) ||
        "";

      if (liveId) {
        console.log(
          `[Socket] session_id resolved after ${waited}ms — sending now`,
        );
        payload.session_id = liveId;
        _dispatchMessage(payload);
        return;
      }

      waited += SESSION_ID_POLL_MS;
      if (waited >= SESSION_ID_WAIT_MS) {
        // Last resort: check one final time for __lastKnownSessionId.
        // This covers mid-interview transcripts that arrive between Q&A turns
        // (after __qaSessionId was cleared but before the next startQARecording).
        const lastKnown =
          (typeof window !== "undefined" && window.__lastKnownSessionId) || "";

        if (lastKnown) {
          console.warn(
            "[Socket] sendInterviewMessage: session_id not live after " +
              `${SESSION_ID_WAIT_MS}ms — using last known session_id "${lastKnown}". ` +
              "Message was: " +
              payload.message,
          );
          payload.session_id = lastKnown;
          _dispatchMessage(payload);
        } else {
          console.error(
            "[Socket] sendInterviewMessage: no session_id available after " +
              `${SESSION_ID_WAIT_MS}ms — dropping message. ` +
              "Message was: " +
              payload.message,
          );
        }
        return;
      }

      setTimeout(pollAndSend, SESSION_ID_POLL_MS);
    };

    setTimeout(pollAndSend, SESSION_ID_POLL_MS);
    return;
  }
  // ─────────────────────────────────────────────────────────────────────────

  _dispatchMessage(payload);
};

/** Internal: serialize and send/retry the fully-resolved payload. */
const _dispatchMessage = (payload) => {
  const serialized = JSON.stringify(payload);

  // ── STEP 1: Try to send directly ───────────────────────────────
  if (interviewSocket?.readyState === WebSocket.OPEN) {
    try {
      interviewSocket.send(serialized);
      return;
    } catch (e) {
      console.warn("[Socket] Send failed — will ping and retry", e);
    }
  }

  // ── STEP 2: Send failed or socket not OPEN — ping to check ─────
  console.warn("[Socket] Probing socket before retry…");
  _pingThenSend(serialized);
};

const _pingThenSend = (serialized) => {
  // If socket can't even accept a ping, go straight to reconnect
  if (!interviewSocket || interviewSocket.readyState !== WebSocket.OPEN) {
    console.warn("[Socket] Socket dead — reconnecting…");
    _reconnectAndSend(serialized);
    return;
  }

  let pongReceived = false;
  const originalOnMessage = interviewSocket.onmessage;

  interviewSocket.onmessage = (event) => {
    if (typeof event.data === "string") {
      try {
        if (JSON.parse(event.data)?.type === "pong") {
          pongReceived = true;
          clearTimeout(pongTimeout);
          interviewSocket.onmessage = originalOnMessage;
          console.log("[Socket] Pong received — retrying send");
          interviewSocket.send(serialized); // ✅ socket alive, resend
          return;
        }
      } catch {
        /* not a pong */
      }
    }
    originalOnMessage?.(event);
  };

  try {
    interviewSocket.send(JSON.stringify({ type: "ping", ts: Date.now() }));
  } catch (e) {
    // Even ping throw — socket is gone
    interviewSocket.onmessage = originalOnMessage;
    _reconnectAndSend(serialized);
    return;
  }

  const pongTimeout = setTimeout(() => {
    if (!pongReceived) {
      console.warn("[Socket] No pong — reconnecting…");
      interviewSocket.onmessage = originalOnMessage;
      _reconnectAndSend(serialized);
    }
  }, 6000);
};

const _reconnectAndSend = (serialized) => {
  // If neither EOI nor LST has arrived yet, an unresponsive/dead socket IS
  // the "unexpected drop" the technical-issue flow exists for. Don't
  // silently self-heal here — let the real close be handled so
  // Interview.js can show that screen and hand off to
  // /connection-restored. Silent self-heal stays available for the (rare)
  // post-LST/post-EOI case — including while a whiteboard round is holding
  // the final EOI message, where a close is expected, not a technical drop.
  if (!eoiReceived && !lstReceived) {
    console.warn(
      "[Socket] Unresponsive before LST/EOI — surfacing as a real close instead of silently reconnecting",
    );
    if (interviewSocket) {
      try {
        if (
          interviewSocket.readyState === WebSocket.OPEN ||
          interviewSocket.readyState === WebSocket.CONNECTING
        ) {
          // Deliberately leave onclose/onerror in place (unlike the self-heal
          // path below) so they fire normally and trigger the technical-issue
          // flow.
          interviewSocket.close(4998, "Unresponsive before EOI");
        } else {
          // Socket is already CLOSED — its onclose already ran (or is about
          // to). Dispatch manually as a fallback in case that already
          // happened while suppression was on, so the technical-issue flow
          // isn't missed.
          window.dispatchEvent(
            new CustomEvent("interview-socket-closed", {
              detail: { eoiReceived: false, lstReceived: false },
            }),
          );
          window.dispatchEvent(new CustomEvent("disable-interview-guard"));
        }
      } catch {
        /* ignore */
      }
    }
    // Don't reconnect, don't resend — the technical-issue flow takes over
    // and the message will naturally resume once the candidate continues.
    return;
  }

  const interviewId = sessionStorage.getItem("interviewId");
  const companyId = sessionStorage.getItem("companyId");

  // FIX: Extract session_id from the message being sent so connectInterviewSocket
  // receives the correct ID even if window.__qaSessionId has been cleared by the
  // time onopen fires (which happens when the reconnect bridges two Q&A sessions).
  let messageSessionId = "";
  try {
    const parsed = JSON.parse(serialized);
    messageSessionId = parsed.session_id || "";
  } catch {
    /* not JSON — leave empty */
  }

  if (interviewSocket) {
    try {
      interviewSocket.onclose = null;
      interviewSocket.onerror = null;
      if (
        interviewSocket.readyState === WebSocket.OPEN ||
        interviewSocket.readyState === WebSocket.CONNECTING
      ) {
        interviewSocket.close(1000, "Reconnecting to send");
      }
    } catch {
      /* ignore */
    }
    interviewSocket = null;
  }

  if (interviewPingInterval) {
    clearInterval(interviewPingInterval);
    interviewPingInterval = null;
  }

  console.log(
    "[Socket] Reconnecting to deliver message… session_id:",
    messageSessionId,
  );
  connectInterviewSocket({
    interview_id: interviewId,
    company_id: companyId,
    meeting_room_id: sessionStorage.getItem("meetingId"),
    // FIX: Pass the session_id from the outgoing message so onopen can use it
    // directly instead of racing against window.__qaSessionId being empty.
    _reconnect_session_id: messageSessionId,
  });

  const onConnected = () => {
    window.removeEventListener("interview-socket-connected", onConnected);
    if (interviewSocket?.readyState === WebSocket.OPEN) {
      console.log("[Socket] Reconnected — delivering message");
      interviewSocket.send(serialized);
    }
  };
  window.addEventListener("interview-socket-connected", onConnected);
};

export const sendAudioToTranscribe = (audioData) => {
  const socket = getTranscribeSocket();
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(audioData);
  } else {
    setTimeout(() => sendAudioToTranscribe(audioData), 500);
  }
};

// FIX: Removed hardcoded session_id: "" which always sent an empty string.
// sendInterviewMessage will read window.__qaSessionId automatically, so the
// correct active session ID is sent even for silence notifications.
export const notifyCandidateNotSpeaking = () => {
  sendInterviewMessage({
    message: "< candidate silent for 30 seconds >",
  });
};

export const closeAllSockets = () => {
  if (interviewSocket && interviewSocket.readyState === WebSocket.OPEN) {
    interviewSocket.close(1000, "Meeting ended");
    interviewSocket = null;
  }

  if (transcribeSocket && transcribeSocket.readyState === WebSocket.OPEN) {
    transcribeSocket.close(1000, "Meeting ended");
    transcribeSocket = null;
  }

  if (transscribePingInterval) {
    clearInterval(transscribePingInterval);
    transscribePingInterval = null;
  }

  interviewQueue = [];
  pendingSpeakerQueue = [];
  messagePublishQueue = [];
  eofReceived = false;
  messageWaitQueue = [];
  isPublishing = false;
  waitingProcessorRunning = false;
  lastPanelSenderId = null;
  postLstHoldActive = false;
  heldInterviewEvents = [];
};

export const getSocketStatus = () => {
  const status = {
    interview: {
      connected: interviewSocket?.readyState === WebSocket.OPEN,
      state: interviewSocket?.readyState || "null",
      queueLength: interviewQueue.length,
    },
    transcribe: {
      connected: transcribeSocket?.readyState === WebSocket.OPEN,
      state: transcribeSocket?.readyState || "null",
    },
  };

  return status;
};
// ===========================
// Moonshot Proctoring Socket
// ===========================

let moonshotSocket = null;
let moonshotTimeoutRef = null;

export const startMoonshotSocket = ({
  interviewId,
  onSuccess,
  onTimeout,
  onError,
  timeoutMs = 15000,
}) => {
  const companyId = sessionStorage.getItem("companyId");
  if (moonshotSocket) {
    moonshotSocket.close();
    moonshotSocket = null;
  }

  const wsUrl = `wss://assess-api-ns.azurewebsites.net/ws/moonshot_status/${interviewId}/${companyId}`;
  moonshotSocket = new WebSocket(wsUrl);

  moonshotSocket.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data);

      if (payload.status === 4) {
        clearTimeout(moonshotTimeoutRef);
        onSuccess?.(payload);
        closeMoonshotSocket();
      }

      if (payload.status === 500) {
        clearTimeout(moonshotTimeoutRef);
        onError?.(payload);
        closeMoonshotSocket();
      }
    } catch (e) {
      console.warn("Moonshot WS parse error", e);
    }
  };

  moonshotSocket.onerror = () => {
    clearTimeout(moonshotTimeoutRef);
    onError?.();
    closeMoonshotSocket();
  };

  moonshotTimeoutRef = setTimeout(() => {
    onTimeout?.();
    closeMoonshotSocket();
  }, timeoutMs);
};
export const closeInterviewSocketWithCallback = (onClosed) => {
  if (!interviewSocket || interviewSocket.readyState === WebSocket.CLOSED) {
    // Already closed — fire callback immediately
    onClosed?.();
    return;
  }

  // Override onclose temporarily to fire the callback once fully closed
  const originalOnClose = interviewSocket.onclose;
  interviewSocket.onclose = (event) => {
    console.warn(`[${Date.now()}] Interview socket closed`); // ← add timestamp
    originalOnClose?.(event); // preserve existing onclose logic
    onClosed?.();
  };

  if (
    interviewSocket.readyState === WebSocket.OPEN ||
    interviewSocket.readyState === WebSocket.CONNECTING
  ) {
    interviewSocket.close(1000, "Meeting ended");
  }
};
export const closeMoonshotSocket = () => {
  if (moonshotTimeoutRef) {
    clearTimeout(moonshotTimeoutRef);
    moonshotTimeoutRef = null;
  }
  if (moonshotSocket) {
    moonshotSocket.close();
    moonshotSocket = null;
  }
};
